import React, { useEffect } from "react";
import Button from "../components/Button.jsx";
import {
    Dialog,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogTitle,
    DialogTrigger,
} from "@shadcn-in-nx/ui/dialog";
import { Input } from "@shadcn-in-nx/ui/input";
import { Label } from "@shadcn-in-nx/ui/label";
import { Textarea } from "@shadcn-in-nx/ui/textarea";
import { Plus, Save, Key, Eye, EyeOff, AlertCircle } from "lucide-react";
import { useBluelagoon } from "@bluelagoon";
import Message from "../components/Message.jsx";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

export function DialogNewSecret({
    onSuccess,
    editingSecret,
    onClose,
    disabled = false,
}) {
    const { t } = useTranslation();
    const [formData, setFormData] = React.useState({
        name: "",
        value: "",
        description: "",
    });
    const [errors, setErrors] = React.useState({});
    const [open, setOpen] = React.useState(false);
    const [showValue, setShowValue] = React.useState(false);

    const {
        call: upsertSecret,
        error,
        isLoading,
    } = useBluelagoon({
        method: "upsertSecret",
    });

    // Handle editing mode
    useEffect(() => {
        if (editingSecret) {
            setFormData({
                name: editingSecret.name,
                value: "", // Never pre-fill value for security
                description: editingSecret.description || "",
            });
            setOpen(true);
        }
    }, [editingSecret]);

    // Handle dialog close
    const handleOpenChange = (isOpen) => {
        setOpen(isOpen);
        if (!isOpen) {
            // Reset form when dialog closes
            setFormData({
                name: "",
                value: "",
                description: "",
            });
            setErrors({});
            setShowValue(false);
            if (onClose) onClose();
        }
    };

    const validateForm = () => {
        const newErrors = {};

        if (!formData.name) {
            newErrors.name = t("secrets.nameRequired");
        } else if (!/^[A-Z][A-Z0-9_]*$/.test(formData.name)) {
            newErrors.name = t("secrets.nameFormat");
        }

        if (!formData.value && !editingSecret) {
            newErrors.value = t("secrets.valueRequired");
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleSaveSecret = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        try {
            const payload = {
                name: formData.name,
                description: formData.description || undefined,
            };

            // Only include value if it's provided (for updates, empty value means keep existing)
            if (formData.value) {
                payload.value = formData.value;
            } else if (editingSecret) {
                // If editing and no new value provided, we need to tell the user
                toast.info(t("secrets.valueNotChanged"));
                // Still proceed if they want to update description only
                if (
                    !formData.description &&
                    editingSecret.description === formData.description
                ) {
                    return;
                }
            }

            // Ensure value is provided for the API
            if (!payload.value) {
                setErrors({ value: t("secrets.valueRequired") });
                return;
            }

            await upsertSecret(payload);
            toast.success(
                editingSecret
                    ? t("secrets.secretUpdated", { name: formData.name })
                    : t("secrets.secretCreated", { name: formData.name })
            );
            handleOpenChange(false);
            if (onSuccess) onSuccess();
        } catch (err) {
            console.error("Error saving secret:", err);
            toast.error(err?.message || t("secrets.failedToSaveSecret"));
        }
    };

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Auto-format name to uppercase and replace invalid characters
        if (name === "name") {
            const formattedValue = value
                .toUpperCase()
                .replace(/[^A-Z0-9_]/g, "_");
            setFormData((prev) => ({
                ...prev,
                [name]: formattedValue,
            }));
        } else {
            setFormData((prev) => ({
                ...prev,
                [name]: value,
            }));
        }

        // Clear errors when user types
        if (errors[name]) {
            setErrors((prev) => ({
                ...prev,
                [name]: undefined,
            }));
        }
    };

    return (
        <Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button disabled={disabled}>
                    <Plus className="mr-2" />
                    {t("secrets.newSecret")}
                </Button>
            </DialogTrigger>
            <DialogContent className="sm:max-w-lg">
                <DialogHeader>
                    <DialogTitle className="flex items-center gap-2">
                        <Key className="w-5 h-5 text-amber-600" />
                        {editingSecret
                            ? t("secrets.editSecret")
                            : t("secrets.createNewSecret")}
                    </DialogTitle>
                    <DialogDescription>
                        {editingSecret
                            ? t("secrets.editSecretDescription")
                            : t("secrets.createSecretDescription")}
                    </DialogDescription>
                </DialogHeader>
                <form onSubmit={handleSaveSecret} className="space-y-4">
                    <div className="space-y-2">
                        <Label htmlFor="name">{t("secrets.secretName")}</Label>
                        <Input
                            id="name"
                            name="name"
                            placeholder={t("secrets.namePlaceholder")}
                            value={formData.name}
                            onChange={handleInputChange}
                            disabled={editingSecret}
                            className="font-mono"
                        />
                        <p className="text-xs text-muted-foreground">
                            {t("secrets.nameHint")}
                        </p>
                        {errors.name && (
                            <p className="text-red-500 text-sm flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {errors.name}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="value">
                            {t("secrets.secretValue")}
                        </Label>
                        <div className="relative">
                            <Input
                                id="value"
                                name="value"
                                type={showValue ? "text" : "password"}
                                placeholder={
                                    editingSecret
                                        ? t("secrets.valuePlaceholderEdit")
                                        : t("secrets.valuePlaceholder")
                                }
                                value={formData.value}
                                onChange={handleInputChange}
                                className="pr-10 font-mono"
                            />
                            <button
                                type="button"
                                onClick={() => setShowValue(!showValue)}
                                className="absolute right-2 top-1/2 -translate-y-1/2 p-1 hover:bg-gray-100 rounded"
                            >
                                {showValue ? (
                                    <EyeOff className="w-4 h-4 text-gray-500" />
                                ) : (
                                    <Eye className="w-4 h-4 text-gray-500" />
                                )}
                            </button>
                        </div>
                        {editingSecret && (
                            <p className="text-xs text-amber-600">
                                {t("secrets.valueWarning")}
                            </p>
                        )}
                        {errors.value && (
                            <p className="text-red-500 text-sm flex items-center gap-1">
                                <AlertCircle className="w-3 h-3" />
                                {errors.value}
                            </p>
                        )}
                    </div>

                    <div className="space-y-2">
                        <Label htmlFor="description">
                            {t("secrets.secretDescription")}
                        </Label>
                        <Textarea
                            id="description"
                            name="description"
                            placeholder={t("secrets.descriptionPlaceholder")}
                            value={formData.description}
                            onChange={handleInputChange}
                            rows={3}
                        />
                        <p className="text-xs text-muted-foreground">
                            {t("secrets.descriptionHint")}
                        </p>
                    </div>

                    {error && (
                        <Message className="text-red-500" error>
                            {error?.message}
                        </Message>
                    )}

                    <DialogFooter className="gap-2">
                        <DialogClose asChild>
                            <Button type="button" variant="secondary">
                                {t("secrets.cancel")}
                            </Button>
                        </DialogClose>
                        <Button
                            type="submit"
                            disabled={isLoading}
                            isLoading={isLoading ? "true" : undefined}
                        >
                            <Save className="mr-2" />
                            {editingSecret
                                ? t("secrets.updateSecret")
                                : t("secrets.createSecret")}
                        </Button>
                    </DialogFooter>
                </form>
            </DialogContent>
        </Dialog>
    );
}
